import { useCallback, useMemo, useState } from 'react';
import type { LoginFormData, LoginFormErrors, LoginValidationRules } from '../types';

// Default validation rules
const DEFAULT_VALIDATION_RULES: LoginValidationRules = {
    email: {
        required: true,
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: 'Please enter a valid email address',
    },
    password: {
        required: true,
        minLength: 6,
        message: 'Password must be at least 6 characters long',
    },
};

export interface UseLoginFormOptions {
    initialValues?: Partial<LoginFormData>;
    validationRules?: Partial<LoginValidationRules>;
    onSubmit?: (data: LoginFormData) => void | Promise<void>;
}

export interface UseLoginFormReturn {
    // Form data
    values: LoginFormData;
    errors: LoginFormErrors;

    // Form state
    isValid: boolean;
    isDirty: boolean;
    isSubmitting: boolean;

    // Form actions
    setValue: (field: keyof LoginFormData, value: string) => void;
    setError: (field: keyof LoginFormErrors, error: string | undefined) => void;
    clearErrors: () => void;
    reset: () => void;
    submit: () => Promise<void>;

    // Field helpers
    getFieldProps: (field: keyof LoginFormData) => {
        value: string;
        onChangeText: (text: string) => void;
        error: string | undefined;
    };
}

export const useLoginForm = ({
    initialValues = {},
    validationRules = {},
    onSubmit,
}: UseLoginFormOptions = {}): UseLoginFormReturn => {
    // Merge validation rules
    const rules = useMemo(() => ({
        ...DEFAULT_VALIDATION_RULES,
        ...validationRules,
    }), [validationRules]);

    // Form state
    const [values, setValues] = useState<LoginFormData>({
        email: initialValues.email || '',
        password: initialValues.password || '',
    });

    const [errors, setErrors] = useState<LoginFormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDirty, setIsDirty] = useState(false);

    // Validation functions
    const validateField = useCallback((field: keyof LoginFormData, value: string): string | undefined => {
        const rule = rules[field];

        if (rule.required && !value.trim()) {
            return `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
        }

        if (field === 'email' && value && 'pattern' in rule && !rule.pattern.test(value)) {
            return rule.message;
        }

        if (field === 'password' && value && 'minLength' in rule && value.length < rule.minLength) {
            return rule.message;
        }

        return undefined;
    }, [rules]);

    const validateForm = useCallback((): LoginFormErrors => {
        const newErrors: LoginFormErrors = {};

        (Object.keys(values) as (keyof LoginFormData)[]).forEach((field) => {
            const error = validateField(field, values[field]);
            if (error) {
                newErrors[field] = error;
            }
        });

        return newErrors;
    }, [values, validateField]);

    // Computed properties
    const isValid = useMemo(() => {
        const formErrors = validateForm();
        return Object.keys(formErrors).length === 0 && !!values.email.trim() && !!values.password.trim();
    }, [validateForm, values]);

    // Form actions
    const setValue = useCallback((field: keyof LoginFormData, value: string) => {
        setValues(prev => ({ ...prev, [field]: value }));
        setIsDirty(true);

        // Clear field error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    }, [errors]);

    const setError = useCallback((field: keyof LoginFormErrors, error: string | undefined) => {
        setErrors(prev => ({ ...prev, [field]: error }));
    }, []);

    const clearErrors = useCallback(() => {
        setErrors({});
    }, []);

    const reset = useCallback(() => {
        setValues({
            email: initialValues.email || '',
            password: initialValues.password || '',
        });
        setErrors({});
        setIsDirty(false);
        setIsSubmitting(false);
    }, [initialValues]);

    const submit = useCallback(async () => {
        if (isSubmitting) return;

        const formErrors = validateForm();
        setErrors(formErrors);

        if (Object.keys(formErrors).length > 0) {
            return;
        }

        if (!onSubmit) return;

        try {
            setIsSubmitting(true);
            await onSubmit(values);
        } catch (error) {
            // Handle submission error
            const errorMessage = error instanceof Error ? error.message : 'An error occurred during login';
            setError('general', errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    }, [isSubmitting, validateForm, onSubmit, values, setError]);

    // Field helpers
    const getFieldProps = useCallback((field: keyof LoginFormData) => ({
        value: values[field],
        onChangeText: (text: string) => setValue(field, text),
        error: errors[field],
    }), [values, errors, setValue]);

    return {
        values,
        errors,
        isValid,
        isDirty,
        isSubmitting,
        setValue,
        setError,
        clearErrors,
        reset,
        submit,
        getFieldProps,
    };
};

export default useLoginForm;
